import React, { useState, useRef, useEffect, useCallback } from 'react'
import { X, Send, Loader2, Sparkles, Mic, MicOff, Paperclip, CheckCircle, AlertCircle, Clock } from 'lucide-react'
import { useAI } from '../../hooks/useAI'
import { useThemeColors } from '../../hooks/useThemeColors'
import { useMobile } from '../../hooks/useMobile'
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition'
import { useVoiceCommand, VoiceCommandResult } from '../../hooks/useVoiceCommand'
import ChatMessage from './ChatMessage'
import QuickActions from './QuickActions'
import { CommandConfirmation } from '../voice/CommandConfirmation'
import { ClarificationDialog } from '../voice/ClarificationDialog'

// CSS keyframes for pulsing animation
const pulseKeyframes = `
@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.15);
    opacity: 0.4;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
}
`

export default function AIAssistant() {
  const { colors } = useThemeColors()
  const { isMobile } = useMobile()
  const { messages, isLoading, error, sendMessage, isOpen, setIsOpen, clearMessages } = useAI()
  const [input, setInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Speech recognition
  const {
    isListening,
    transcript,
    interimTranscript,
    startListening,
    stopListening,
    resetTranscript,
    isSupported: speechSupported,
    error: speechError
  } = useSpeechRecognition()

  // Voice command processing
  const {
    currentCommand,
    recentCommands,
    isProcessing: isCommandProcessing,
    lastResult,
    processCommand,
    confirmAction,
    cancelCommand,
    loadRecentCommands,
    clearResult
  } = useVoiceCommand()

  // Voice mode state
  const [isVoiceMode, setIsVoiceMode] = useState(false)
  const [showVoiceHint, setShowVoiceHint] = useState(false)

  // Add keyframes to document
  useEffect(() => {
    const styleId = 'ai-assistant-pulse-style'
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style')
      style.id = styleId
      style.textContent = pulseKeyframes
      document.head.appendChild(style)
    }
  }, [])

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, lastResult])

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
      loadRecentCommands()
    }
  }, [isOpen, loadRecentCommands])

  // Update input when transcript changes
  useEffect(() => {
    if (transcript) {
      setInput(transcript)
    }
  }, [transcript])

  // Auto-submit when speech ends with complete transcript
  useEffect(() => {
    if (!isListening && transcript && isVoiceMode) {
      // Small delay to allow final transcript to settle
      const timer = setTimeout(() => {
        if (transcript.trim()) {
          handleVoiceSubmit(transcript.trim())
        }
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [isListening, transcript, isVoiceMode])

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!input.trim() || isLoading || isCommandProcessing) return

    const message = input.trim()
    setInput('')
    resetTranscript()

    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
    }

    await sendMessage(message)
  }

  const handleVoiceSubmit = async (text: string) => {
    if (!text || isLoading || isCommandProcessing) return

    setInput('')
    resetTranscript()
    setIsVoiceMode(false)

    // Process as voice command for booking-focused AI
    await processCommand(text)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleQuickAction = async (action: string) => {
    await sendMessage(action)
  }

  const toggleVoiceInput = useCallback(() => {
    if (!speechSupported) {
      alert('Speech recognition is not supported in this browser. Please use Chrome or Edge.')
      return
    }

    if (isListening) {
      stopListening()
      setIsVoiceMode(false)
    } else {
      setIsVoiceMode(true)
      setShowVoiceHint(true)
      resetTranscript()
      startListening()
      // Hide hint after 3 seconds
      setTimeout(() => setShowVoiceHint(false), 3000)
    }
  }, [isListening, speechSupported, startListening, stopListening, resetTranscript])

  const handleConfirm = useCallback(async () => {
    if (!currentCommand || !lastResult) return
    const actionData = lastResult.booking || lastResult.registration || lastResult
    await confirmAction(currentCommand.id, actionData)
  }, [currentCommand, lastResult, confirmAction])

  const handleCancel = useCallback(async () => {
    if (currentCommand) {
      await cancelCommand(currentCommand.id)
    }
    clearResult()
  }, [currentCommand, cancelCommand, clearResult])

  const handleClarificationSelect = useCallback(async (option: { createNew?: boolean; name?: string; id?: string }) => {
    const newQuery = option.createNew
      ? `Create new customer named "${currentCommand?.rawText?.match(/for\s+([^,]+)/)?.[1] || 'Unknown'}"`
      : `Use ${option.name || option.id} for the booking`
    await processCommand(newQuery)
  }, [currentCommand, processCommand])

  if (!isOpen) return null

  return (
    <div className="ai-assistant-container" style={{
      position: 'fixed',
      bottom: isMobile ? '0' : '20px',
      right: isMobile ? '0' : '20px',
      left: isMobile ? '0' : 'auto',
      top: isMobile ? '0' : 'auto',
      width: isMobile ? '100%' : '400px',
      height: isMobile ? '100%' : '600px',
      backgroundColor: colors.background,
      borderRadius: isMobile ? '0' : '12px',
      boxShadow: isMobile ? 'none' : '0 8px 32px rgba(34, 211, 238, 0.16)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1000,
      border: isMobile ? 'none' : `1px solid ${colors.border}`,
      opacity: 1,
      // Safe area insets for iOS notch
      paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
      paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Sparkles size={20} style={{ color: colors.primary }} />
          <h3 style={{ color: colors.text, margin: 0, fontSize: '16px', fontWeight: 600 }}>
            AI Assistant
          </h3>
          {isListening && (
            <span style={{
              fontSize: '12px',
              color: colors.primary,
              backgroundColor: `${colors.primary}20`,
              padding: '2px 8px',
              borderRadius: '12px',
              animation: 'pulse-ring 1.5s ease-in-out infinite'
            }}>
              Listening...
            </span>
          )}
        </div>
        <button
          onClick={() => setIsOpen(false)}
          style={{
            background: 'none',
            border: 'none',
            color: colors.textSecondary,
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '4px',
            transition: 'background-color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
          <X size={20} />
        </button>
      </div>

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {messages.length === 0 && !lastResult && (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: colors.textSecondary
          }}>
            <Sparkles size={48} style={{ color: colors.primary, marginBottom: '16px' }} />
            <p style={{ fontSize: '16px', marginBottom: '16px' }}>
              Hi! I'm your AI assistant. I can help you with:
            </p>
            <QuickActions onAction={handleQuickAction} />

            {/* Voice command examples */}
            {speechSupported && (
              <div style={{ marginTop: '24px', textAlign: 'left' }}>
                <p style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: colors.text,
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Mic size={16} style={{ color: colors.primary }} />
                  Or try voice commands:
                </p>
                <div style={{
                  fontSize: '13px',
                  color: colors.textSecondary,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}>
                  <p>"Book room 1 for John Smith tomorrow at 3pm"</p>
                  <p>"Add Emma to Tuesday's kids class"</p>
                  <p>"Is room 2 free Friday afternoon?"</p>
                  <p>"Cancel the 4pm booking today"</p>
                </div>
              </div>
            )}
          </div>
        )}

        {messages.map((message) => (
          <ChatMessage key={message.id} message={message} />
        ))}

        {/* Voice Command Result - Confirmation */}
        {lastResult && lastResult.status === 'ready_for_confirmation' && (lastResult.booking || lastResult.registration) && (
          <CommandConfirmation
            type={lastResult.action || 'create_booking'}
            data={lastResult}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            isConfirming={isCommandProcessing}
          />
        )}

        {/* Voice Command Result - Clarification */}
        {lastResult && lastResult.clarification && (
          <ClarificationDialog
            type={lastResult.clarification.type}
            question={lastResult.clarification.question}
            options={lastResult.clarification.options}
            onSelect={handleClarificationSelect}
            onCancel={handleCancel}
          />
        )}

        {/* Voice Command Result - Conflict */}
        {lastResult && lastResult.conflict && (
          <div
            style={{
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid rgba(245, 158, 11, 0.2)',
              backgroundColor: 'rgba(245, 158, 11, 0.1)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <AlertCircle size={20} style={{ color: '#f59e0b' }} />
              <span style={{ fontWeight: 600, color: colors.text }}>Conflict Detected</span>
            </div>
            <p style={{ color: colors.textSecondary, marginBottom: '8px' }}>{lastResult.conflict.message}</p>
            {lastResult.conflict.existingBookings && (
              <div style={{ marginTop: '8px' }}>
                {lastResult.conflict.existingBookings.map((b: { title: string; startTime: string; endTime: string }, i: number) => (
                  <div key={i} style={{ fontSize: '13px', color: colors.textSecondary }}>
                    {b.title}: {new Date(b.startTime).toLocaleTimeString()} - {new Date(b.endTime).toLocaleTimeString()}
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={handleCancel}
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                borderRadius: '6px',
                border: `1px solid ${colors.border}`,
                background: 'none',
                color: colors.textSecondary,
                cursor: 'pointer'
              }}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Voice Command Result - Success */}
        {lastResult && lastResult.status === 'executed' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              backgroundColor: 'rgba(16, 185, 129, 0.1)'
            }}
          >
            <CheckCircle size={20} style={{ color: '#10b981' }} />
            <span style={{ color: colors.text }}>
              {lastResult.message || 'Action completed successfully!'}
            </span>
          </div>
        )}

        {/* Voice Command Result - Error */}
        {lastResult && lastResult.error && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px',
              borderRadius: '8px',
              border: `1px solid ${colors.error}33`,
              backgroundColor: `${colors.error}1A`
            }}
          >
            <AlertCircle size={20} style={{ color: colors.error }} />
            <span style={{ color: colors.text }}>{lastResult.error}</span>
          </div>
        )}

        {/* Voice Command Result - Info Message */}
        {lastResult && lastResult.message && !lastResult.error && lastResult.status !== 'executed' &&
         lastResult.status !== 'ready_for_confirmation' && !lastResult.clarification && (
          <div
            style={{
              padding: '16px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`
            }}
          >
            <p style={{ color: colors.text }}>{lastResult.message}</p>
          </div>
        )}

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: `${colors.error}1A`,
            border: `1px solid ${colors.error}4D`,
            borderRadius: '8px',
            color: colors.error
          }}>
            {error}
          </div>
        )}

        {speechError && (
          <div style={{
            padding: '12px',
            backgroundColor: `${colors.error}1A`,
            border: `1px solid ${colors.error}4D`,
            borderRadius: '8px',
            color: colors.error
          }}>
            {speechError}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Voice hint tooltip */}
      {showVoiceHint && (
        <div style={{
          position: 'absolute',
          bottom: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: colors.backgroundDark,
          padding: '8px 16px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(34, 211, 238, 0.12)',
          color: colors.text,
          fontSize: '13px',
          whiteSpace: 'nowrap',
          zIndex: 10
        }}>
          Speak now... Click mic again to stop
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} style={{
        padding: '16px',
        borderTop: `1px solid ${colors.border}`,
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-end'
      }}>
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'flex-end',
          backgroundColor: colors.inputBackground,
          borderRadius: '8px',
          border: `1px solid ${isListening ? colors.primary : colors.border}`,
          padding: '8px 12px',
          transition: 'border-color 0.2s'
        }}>
          <textarea
            ref={inputRef}
            value={interimTranscript ? input + interimTranscript : input}
            onChange={(e) => {
              setInput(e.target.value)
              // Auto-resize textarea
              e.target.style.height = 'auto'
              e.target.style.height = `${e.target.scrollHeight}px`
            }}
            onKeyDown={handleKeyDown}
            placeholder={isListening ? "Listening..." : "Ask me anything..."}
            rows={1}
            disabled={isListening}
            style={{
              flex: 1,
              background: 'none',
              border: 'none',
              outline: 'none',
              color: colors.text,
              fontSize: '14px',
              resize: 'none',
              minHeight: '24px',
              maxHeight: '120px',
              fontFamily: 'inherit',
              lineHeight: '1.5',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word'
            }}
          />
          <button
            type="button"
            style={{
              background: 'none',
              border: 'none',
              color: colors.textSecondary,
              cursor: 'pointer',
              padding: '4px',
              marginLeft: '4px'
            }}
            title="Attach file"
          >
            <Paperclip size={18} />
          </button>
          <button
            type="button"
            onClick={toggleVoiceInput}
            disabled={!speechSupported}
            style={{
              background: isListening ? colors.primary : 'none',
              border: 'none',
              color: isListening ? 'white' : speechSupported ? colors.textSecondary : colors.textMuted,
              cursor: speechSupported ? 'pointer' : 'not-allowed',
              padding: '6px',
              marginLeft: '4px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              position: 'relative'
            }}
            title={speechSupported ? (isListening ? "Stop listening" : "Voice input") : "Voice not supported in this browser"}
          >
            {/* Pulse ring when listening */}
            {isListening && (
              <span style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                backgroundColor: colors.primary,
                animation: 'pulse-ring 1.5s ease-in-out infinite',
                opacity: 0.4
              }} />
            )}
            {isListening ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
        </div>
        <button
          type="submit"
          disabled={(!input.trim() && !interimTranscript) || isLoading || isCommandProcessing}
          style={{
            backgroundColor: colors.primary,
            color: colors.background,
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            cursor: (input.trim() || interimTranscript) && !isLoading && !isCommandProcessing ? 'pointer' : 'not-allowed',
            opacity: (input.trim() || interimTranscript) && !isLoading && !isCommandProcessing ? 1 : 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          {isLoading || isCommandProcessing ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Send size={18} />
          )}
        </button>
      </form>
    </div>
  )
}
